import styled from 'styled-components'
import Color from 'color'

export const ButtonWrapper = styled.div`
  &.fluid {
    width: 100%;
  }
`

export const StyledButton = styled(({ iconColor, ...props }) => (
  <button {...props} />
))<{
  iconColor?: string
}>`
  transition: all 0.15s ease;
  position: relative;
  border: none;
  height: var(--height);
  border-radius: var(--border-radius);
  padding: 0 var(--padding);
  cursor: pointer;
  display: flex;
  align-items: center;

  svg {
    width: var(--svg-size);
    height: var(--svg-size);
    margin-right: var(--svg-margin);
    margin-top: 0 !important;
    stroke: ${({ theme, iconColor }) => iconColor ?? theme.secondaryFontColor};
  }

  label {
    pointer-events: none;
    color: ${({ theme }) => theme.secondaryFontColor};
    font-size: var(--font-size);
    font-weight: 500;
    white-space: nowrap;

    + svg {
      margin-left: var(--svg-margin);
      margin-right: 0;
    }
  }

  &:hover {
    box-shadow: ${({ theme }) =>
      `0 1px 3px 0 ${theme.primaryColor}26, 0 1px 1px 0 rgba(0, 0, 0, 0.1)`};
  }

  &:focus-visible {
    outline: none;
    z-index: 1;

    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      border-radius: var(--border-radius);
    }
  }

  &.primary {
    background-color: ${({ theme }) => theme.primaryColor};

    svg {
      stroke: ${({ theme, iconColor }) =>
        iconColor ?? theme.secondaryFontColor};
    }

    &:hover {
      background-color: ${({ theme }) =>
        Color(theme.primaryColor).darken(0.1).toString()};
    }

    &:active {
      background-color: ${({ theme }) =>
        Color(theme.primaryColor).darken(0.2).toString()};
    }

    &:focus-visible {
      outline: none;

      &::before {
        outline: 2px solid white;
        z-index: 2;
      }

      &::after {
        outline: ${({ theme }) => `4px solid ${theme.primaryColor}`};
        z-index: 1;
      }
    }
  }

  &.outlined,
  &.outlined-brand,
  &.light-brand,
  &.transparent {
    svg {
      stroke: ${({ theme, iconColor }) => iconColor ?? theme.primaryColor};
    }

    &:focus-visible {
      &::before {
        outline: none;
      }

      &::after {
        outline: ${({ theme }) => `2px solid ${theme.primaryColor}`};
      }
    }
  }

  &.outlined,
  &.outlined-brand {
    background-color: #fff;
    border: 1px solid #e6e8eb;

    &:hover {
      background-color: #f9f9fa;
    }

    &:active {
      background-color: #f3f3f5;
    }
  }

  &.outlined {
    label {
      color: #525b75;
    }
  }

  &.outlined-brand {
    label {
      color: ${({ theme }) => theme.primaryColor};
    }
  }

  &.light-brand,
  &.transparent {
    label {
      color: ${({ theme }) => theme.primaryColor};
    }
  }

  &.light-brand {
    background-color: ${({ theme }) => `${theme.primaryColor}0d`};

    &:hover {
      background-color: ${({ theme }) => `${theme.primaryColor}17`};
    }

    &:active {
      background-color: ${({ theme }) => `${theme.primaryColor}26`};
    }
  }

  &.transparent {
    background-color: transparent;

    &:hover {
      background-color: ${({ theme }) => `${theme.primaryColor}0d`};
    }

    &:active {
      background-color: ${({ theme }) => `${theme.primaryColor}17`};
    }
  }

  &.no-content {
    svg {
      margin-right: 0;
      margin-left: 0;
    }
  }

  &.fluid {
    width: 100%;
    padding: 0;
    justify-content: center;
  }

  &.cancel {
    background-color: #f3f3f5;

    &:hover {
      background-color: #e6e8eb;
    }
  }

  &.cancel {
    label {
      color: #525b75;
    }
  }

  &.cancel {
    svg {
      stroke: #525b75;
    }
  }

  &.loading {
    label,
    svg {
      visibility: hidden;
    }
  }

  &:disabled:not(.loading) {
    opacity: 0.4;
  }

  &:disabled {
    cursor: default;

    &:hover {
      box-shadow: none;

      &.primary {
        background-color: ${({ theme }) => theme.primaryColor};
      }

      &.outlined,
      &.outlined-brand {
        border: 1px solid #e6e8eb;
        background-color: #fff;
      }
    }
  }
`

export const StyledButtonGroup = styled.div`
  display: flex;
  gap: 8px;

  &.vertical {
    flex-direction: column;

    ${StyledButton} {
      width: 100%;
    }
  }

  &.packed-tightly {
    gap: 0;

    ${StyledButton} {
      border-radius: 0;
    }

    &:not(.vertical) {
      ${StyledButton} {
        &.outlined,
        &.outlined-brand {
          border-left: 1px solid #e6e8eb;
          border-right: none;
        }
      }

      > div:first-child {
        ${StyledButton} {
          border-top-left-radius: var(--border-radius);
          border-bottom-left-radius: var(--border-radius);
        }
      }

      > div:last-child {
        ${StyledButton} {
          border-top-right-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);

          &.outlined,
          &.outlined-brand {
            border-right: 1px solid #e6e8eb;
          }
        }
      }
    }

    &.vertical {
      ${StyledButton} {
        &.outlined,
        &.outlined-brand {
          border-top: 1px solid #e6e8eb;
          border-bottom: none;
        }
      }

      > div:first-child {
        ${StyledButton} {
          border-top-left-radius: var(--border-radius);
          border-top-right-radius: var(--border-radius);
        }
      }

      > div:last-child {
        ${StyledButton} {
          border-bottom-left-radius: var(--border-radius);
          border-bottom-right-radius: var(--border-radius);

          &.outlined,
          &.outlined-brand {
            border-bottom: 1px solid #e6e8eb;
          }
        }
      }
    }
  }
`

export const NumberBubble = styled.span`
  margin-left: 8px;
  display: inline-block;
  background: ${props => props.theme.secondaryFontColor};
  color: ${props => props.theme.primaryColor};
  font-size: 0.8em;
  padding: 0px 7px;
  border-radius: 10px;
  font-weight: bold;
  height: 17px;
  line-height: 17px;

  &.notify {
    position: absolute;
    top: -8px;
    right: -8px;
    border-radius: 50%;
    background: #ff9186;
    color: #fff;
  }
`
